import React, { forwardRef, useMemo, useState } from 'react';

import { MimeType } from '@aline/common';
import type { Theme } from '@mui/material';
import { alpha, DialogActions, DialogContent, Typography, useTheme } from '@mui/material';
import type { GridColDef, GridRowSelectionModel, GridToolbarProps } from '@mui/x-data-grid-premium';
import { DataGridPremium, gridClasses } from '@mui/x-data-grid-premium';
import clsx from 'clsx';
import { CircleAlert } from 'lucide-react';
import { FileTypeIcon } from 'src/components';
import { AlineButton, AlineDialog, Spacer } from 'src/components/generic';
import { SynchronizeIcon } from 'src/components/icons/SynchronizeIcon';
import { Apollo } from 'src/gql/generated';
import { createDataGridPremiumOverrideStyles, useGetSelectedRowIds } from 'src/hooks';
import { makeStyles } from 'tss-react/mui';

const createFailedFilesDataGridStyles = (theme: Theme) => ({
  ...createDataGridPremiumOverrideStyles(theme),
  [`& .${gridClasses.main}`]: {
    backgroundColor: theme.palette.common.white,
  },
});

const useStyles = makeStyles()(theme => ({
  container: {
    minWidth: 800,
    width: '100%',
  },
  content: {
    padding: theme.spacing(2, 3),
  },
  dialogActions: {
    justifyContent: 'flex-end',
    padding: theme.spacing(2, 3, 3),
  },
  documentCell: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(1),
  },
  grid: {
    height: 400,
    width: '100%',
  },
  header: {
    alignItems: 'center',
    backgroundColor: theme.palette.background.default,
    display: 'flex',
    gap: theme.spacing(2),
  },
  headerCount: {
    color: theme.palette.text.secondary,
    fontWeight: 'normal',
  },
  headerTitle: {
    fontWeight: 600,
  },
}));

const useErrorChipStyles = makeStyles()(theme => ({
  chip: {
    '&:hover': {
      backgroundColor: alpha(theme.palette.error.main, 0.25),
    },
    alignItems: 'center',
    backgroundColor: alpha(theme.palette.error.main, 0.15),
    // Border makes chip same height as label chip to the left of it...
    border: `1px solid transparent`,
    borderRadius: 8,
    cursor: 'pointer',
    display: 'flex',
    gap: theme.spacing(0.5),
    overflow: 'hidden',
    padding: theme.spacing(0.25, 0.75),
    width: 'fit-content',
  },
  chipText: {
    color: theme.palette.error.main,
    fontSize: 12,
    fontWeight: 500,
  },
}));

interface ErrorChipProps {
  chipText: string;
  onClick: (e: React.MouseEvent) => void;
  className?: string;
}

const ErrorChip = forwardRef<HTMLDivElement, ErrorChipProps>(function ErrorChip(
  { chipText, className, onClick },
  ref
) {
  const theme = useTheme();
  const { classes } = useErrorChipStyles();

  return (
    <div
      ref={ref}
      className={clsx(classes.chip, className)}
      onClick={e => {
        e.stopPropagation();
        onClick(e);
      }}
    >
      <CircleAlert color={theme.palette.error.main} size={16} strokeWidth={2} />
      <Typography noWrap className={classes.chipText}>
        {chipText}
      </Typography>
    </div>
  );
});

const columnDefs: GridColDef<Apollo.FailedFile>[] = [
  {
    field: 'fileName',
    flex: 1,
    headerName: 'Document',
    minWidth: 300,
    renderCell: params => (
      <div style={{ alignItems: 'center', display: 'flex', gap: 8 }}>
        <div>
          <FileTypeIcon mimetype={params.row.mimeType ?? MimeType.TEXT_PLAIN} />
        </div>
        <Typography variant="body2">{params.value}</Typography>
      </div>
    ),
  },
  {
    field: 'failedReason',
    flex: 1,
    headerName: 'Reason',
    minWidth: 200,
    renderCell: params => (
      <Typography color="textSecondary" variant="body2">
        {params.value}
      </Typography>
    ),
  },
];

interface FailedFilesDialogProps {
  jobId: UUID;
  onClose: () => void;
  open: boolean;
  rows: Apollo.FailedFile[];
}

export function FailedFilesDialog({
  jobId,
  onClose,
  open,
  rows,
}: FailedFilesDialogProps): JSX.Element {
  const theme = useTheme();
  const { classes } = useStyles();
  const [selectedFiles, setSelectedFiles] = useState<GridRowSelectionModel>({
    ids: new Set([]),
    type: 'include',
  });

  return (
    <AlineDialog
      classes={{ paper: classes.container }}
      contentPaddingPx={0}
      maxWidth="md"
      open={open}
      title="Failed Documents"
      onClose={onClose}
    >
      <DialogContent className={classes.content}>
        <div className={classes.grid}>
          <DataGridPremium<Apollo.FailedFile>
            checkboxSelection
            disableColumnFilter
            disableColumnMenu
            disableColumnReorder
            disableColumnResize
            hideFooter
            showToolbar
            columns={columnDefs}
            getRowHeight={() => 'auto'}
            getRowId={row => row.fileId}
            rowSelectionModel={selectedFiles}
            rows={rows}
            slotProps={{
              loadingOverlay: {
                noRowsVariant: 'linear-progress',
                variant: 'linear-progress',
              },
              toolbar: {
                jobId,
                onClose,
              },
            }}
            slots={{
              toolbar: FailedFilesToolbar,
            }}
            sx={createFailedFilesDataGridStyles(theme)}
            onRowSelectionModelChange={setSelectedFiles}
          />
        </div>
      </DialogContent>
      <DialogActions className={classes.dialogActions}>
        <AlineButton color="primary" variant="contained" onClick={() => onClose()}>
          Done
        </AlineButton>
      </DialogActions>
    </AlineDialog>
  );
}

interface FailedFilesToolbarProps extends GridToolbarProps {
  jobId?: UUID;
  onClose?: () => void;
}

function FailedFilesToolbar({ jobId, onClose }: FailedFilesToolbarProps): JSX.Element | null {
  const theme = useTheme();
  const { classes } = useStyles();
  const [retryFolderSyncFailedFiles, { loading: retryingFailedFiles }] =
    Apollo.useRetryFolderSyncFailedFilesMutation();
  const [dismissFailedFiles, { loading: dismissingFailedFiles }] =
    Apollo.useDismissFailedFilesMutation();
  const { selectedRowIds: selectedFileIds } = useGetSelectedRowIds();
  const isSomeFileSelected = Boolean(selectedFileIds.length);

  return (
    <div className={classes.header}>
      <Spacer />
      <AlineButton
        color="primary"
        loading={dismissingFailedFiles || retryingFailedFiles}
        startIcon={
          !isSomeFileSelected ? <SynchronizeIcon fill={theme.palette.primary.main} /> : undefined
        }
        variant="outlined"
        onClick={async e => {
          e.stopPropagation();

          if (!jobId) return;
          if (selectedFileIds.length) {
            await dismissFailedFiles({
              variables: { fileIds: selectedFileIds, folderSyncJobId: jobId },
            });
            return;
          }

          await retryFolderSyncFailedFiles({
            variables: { folderSyncJobId: jobId },
          });
          onClose?.();
        }}
      >
        {selectedFileIds.length > 0 ? `Dismiss ${selectedFileIds.length} Files` : 'Retry All'}
      </AlineButton>
    </div>
  );
}

interface FailedFilesChipProps {
  job: Pick<Apollo.FolderSyncJob, 'id' | 'failedFiles' | '__typename'>;
}

export function FailedFilesChip({ job }: FailedFilesChipProps): JSX.Element | null {
  const [open, setOpen] = useState(false);
  const rows = useMemo(() => job.failedFiles.filter(f => !f.isDimissed), [job.failedFiles]);

  if (!rows.length) return null;

  return (
    <>
      <ErrorChip
        chipText={rows.length > 1 ? `View ${rows.length} failed files` : 'View 1 failed file'}
        onClick={() => setOpen(true)}
      />
      <FailedFilesDialog jobId={job.id} open={open} rows={rows} onClose={() => setOpen(false)} />
    </>
  );
}
